/**
 * 来場者新規登録フォームフック
 * 入力検証(パスワード確認含む)を含む新規登録UI状態を管理します
 * エラーは1件だけ持ち、どの欄に紐づくか（欄に紐づかない場合は'form'）を併せて返します
 */

import { useCallback, useEffect, useState } from 'react';
import {
  VISITOR_REGISTER_CHECK_ERROR_MESSAGE,
  VISITOR_REGISTER_FAILED_ERROR_MESSAGE,
  VISITOR_REGISTER_PASSWORD_MISMATCH_ERROR_MESSAGE,
  VISITOR_REGISTER_RATE_LIMIT_ERROR_MESSAGE,
  VISITOR_REGISTER_TERMS_NOT_ACCEPTED_ERROR_MESSAGE,
  VISITOR_REGISTER_TERMS_VERSION_OUTDATED_ERROR_MESSAGE,
  VISITOR_REGISTER_USERNAME_TAKEN_ERROR_MESSAGE,
  VISITOR_TERMS_FETCH_ERROR_MESSAGE,
} from '../constants';
import {
  getActiveTerms,
  visitorRegister,
  VISITOR_REGISTER_INVALID_INPUT_CODE,
  VISITOR_REGISTER_RATE_LIMIT_CODE,
  VISITOR_REGISTER_TERMS_NOT_ACCEPTED_CODE,
  VISITOR_REGISTER_TERMS_VERSION_OUTDATED_CODE,
  VISITOR_REGISTER_USERNAME_TAKEN_CODE,
} from '../services/visitorAuthService';
import { saveVisitorSession } from '../services/visitorSessionService';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import {
  truncateVisitorUsername,
  validateVisitorPassword,
  validateVisitorUsername,
} from '../../../shared/utils/validation';

/**
 * エラーを表示する場所
 * 入力欄（上から ユーザ名 → パスワード → 確認 → 規約同意）のいずれか、または欄に紐づかない'form'
 */
export type RegisterErrorField = 'username' | 'password' | 'passwordConfirmation' | 'terms' | 'form';

/**
 * 画面に表示するエラー（同時に1件だけ）
 * isUsernameTakenは、登録済みの人をログインへ案内するためにユーザ名重複だけを区別する
 */
export type RegisterFormError = { field: RegisterErrorField; message: string; isUsernameTaken?: boolean };

/** focusを移す先になる欄（'form'は対応する欄が無いため含まない） */
export type RegisterInvalidField = Exclude<RegisterErrorField, 'form'>;

/** 登録送信結果 */
type RegisterSubmitResult = {
  isSuccess: boolean;
  payload?: { visitorId: string, nickname: string, currentPoint: number, maxReachedPoint: number, sessionToken: string, sessionExpiresAt: string };
  invalidField?: RegisterInvalidField;
};

/**
 * 来場者新規登録フォーム状態を返す
 * @returns {{
 * username: string,
 * password: string,
 * passwordConfirmation: string,
 * terms: { termsId: string, version: string, title: string, content: string, effectiveAt: string } | null,
 * isTermsLoading: boolean,
 * termsErrorMessage: string,
 * isTermsAccepted: boolean,
 * isTermsModalVisible: boolean,
 * formError: RegisterFormError | null,
 * isSubmitting: boolean,
 * isSubmitDisabled: boolean,
 * handleUsernameChange: (value: string) => void,
 * handlePasswordChange: (value: string) => void,
 * handlePasswordConfirmationChange: (value: string) => void,
 * handleToggleTermsAccepted: () => void,
 * handleOpenTermsModal: () => void,
 * handleCloseTermsModal: () => void,
 * handleRetryTerms: () => Promise<void>,
 * submitRegister: () => Promise<RegisterSubmitResult>,
 * }}
 */
export const useVisitorRegisterForm = () => {
  /** ユーザ名入力値 */
  const [username, setUsername] = useState('');

  /** パスワード入力値 */
  const [password, setPassword] = useState('');

  /** パスワード確認入力値 */
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  /** 現在有効な利用規約 */
  const [terms, setTerms] = useState(null);

  /** 利用規約取得中状態 */
  const [isTermsLoading, setIsTermsLoading] = useState(true);

  /** 利用規約取得エラーメッセージ */
  const [termsErrorMessage, setTermsErrorMessage] = useState('');

  /** 利用規約同意状態 */
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  /** 利用規約モーダル表示状態 */
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);

  /** 画面表示エラー（表示場所の欄とメッセージ） */
  const [formError, setFormError] = useState<RegisterFormError | null>(null);

  /** 送信中状態 */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * 現在有効な利用規約を取得する
   * @returns {Promise<void>}
   */
  const handleRetryTerms = useCallback(async () => {
    setIsTermsLoading(true);
    setTermsErrorMessage('');
    setTerms(null);
    // 再取得した規約は版が更新されている可能性があるため、同意をやり直してもらう
    setIsTermsAccepted(false);
    setIsTermsModalVisible(false);

    try {
      /** 取得した利用規約 */
      const activeTerms = await getActiveTerms();
      setTerms(activeTerms);
    } catch (error) {
      logError('来場者新規登録画面の利用規約取得に失敗しました。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      setTermsErrorMessage(
        error instanceof AppError && error.message
          ? error.message
          : VISITOR_TERMS_FETCH_ERROR_MESSAGE,
      );
    } finally {
      setIsTermsLoading(false);
    }
  }, []);

  useEffect(() => {
    handleRetryTerms();
  }, [handleRetryTerms]);

  /**
   * ユーザ名入力変更時の処理
   * @param {string} value - 入力値
   * @returns {void}
   */
  const handleUsernameChange = useCallback((value) => {
    setUsername(truncateVisitorUsername(value));
    setFormError(null);
  }, []);

  /**
   * パスワード入力変更時の処理
   * @param {string} value - 入力値
   * @returns {void}
   */
  const handlePasswordChange = useCallback((value) => {
    setPassword(value);
    setFormError(null);
  }, []);

  /**
   * パスワード確認入力変更時の処理
   * @param {string} value - 入力値
   * @returns {void}
   */
  const handlePasswordConfirmationChange = useCallback((value) => {
    setPasswordConfirmation(value);
    setFormError(null);
  }, []);

  /**
   * 利用規約同意状態を切り替える
   * 全文モーダルを開いたかどうかは同意の条件にしない（定型約款の表示はチェック横のリンクとモーダルで行う）
   * @returns {void}
   */
  const handleToggleTermsAccepted = useCallback(() => {
    setIsTermsAccepted((currentValue) => !currentValue);
    setFormError(null);
  }, []);

  /**
   * 利用規約モーダルを開く
   * @returns {void}
   */
  const handleOpenTermsModal = useCallback(() => {
    if (terms) {
      setIsTermsModalVisible(true);
    }
  }, [terms]);

  /**
   * 利用規約モーダルを閉じる
   * @returns {void}
   */
  const handleCloseTermsModal = useCallback(() => {
    setIsTermsModalVisible(false);
  }, []);

  /**
   * エラーを1件表示し、失敗の送信結果を返す
   * @param {RegisterErrorField} field - 表示場所の欄
   * @param {string} message - 表示メッセージ
   * @param {{ isUsernameTaken?: boolean }} [options] - ユーザ名重複かどうか
   * @returns {RegisterSubmitResult} 失敗の送信結果（欄に紐づく場合はfocus先を含む）
   */
  const rejectSubmit = useCallback((
    field: RegisterErrorField,
    message: string,
    { isUsernameTaken = false }: { isUsernameTaken?: boolean } = {},
  ): RegisterSubmitResult => {
    setFormError({ field, message, isUsernameTaken });
    return { isSuccess: false, invalidField: field === 'form' ? undefined : field };
  }, []);

  /**
   * 新規登録送信処理
   * 入力欄の上から順に検証し、最初の1件だけを案内する
   * @returns {Promise<RegisterSubmitResult>} 送信結果
   */
  const submitRegister = useCallback(async (): Promise<RegisterSubmitResult> => {
    /** ユーザ名検証結果 */
    const usernameValidationResult = validateVisitorUsername(username);

    if (!usernameValidationResult.isValid) {
      return rejectSubmit('username', usernameValidationResult.message);
    }

    /** パスワード検証結果 */
    const passwordValidationResult = validateVisitorPassword(password);

    if (!passwordValidationResult.isValid) {
      return rejectSubmit('password', passwordValidationResult.message);
    }

    if (password !== passwordConfirmation) {
      return rejectSubmit('passwordConfirmation', VISITOR_REGISTER_PASSWORD_MISMATCH_ERROR_MESSAGE);
    }

    if (isTermsLoading || !terms || termsErrorMessage) {
      return rejectSubmit('terms', VISITOR_TERMS_FETCH_ERROR_MESSAGE);
    }

    if (!isTermsAccepted) {
      return rejectSubmit('terms', VISITOR_REGISTER_TERMS_NOT_ACCEPTED_ERROR_MESSAGE);
    }

    setIsSubmitting(true);

    try {
      /** 登録結果 */
      const registerResult = await visitorRegister(
        username,
        password,
        terms.version,
        isTermsAccepted,
      );
      await saveVisitorSession({
        visitorId: registerResult.visitorId,
        nickname: registerResult.nickname,
        sessionToken: registerResult.sessionToken,
        sessionExpiresAt: registerResult.sessionExpiresAt,
      });

      setFormError(null);
      return { isSuccess: true, payload: registerResult };
    } catch (error) {
      if (error instanceof AppError) {
        if (error.code === VISITOR_REGISTER_INVALID_INPUT_CODE) {
          return rejectSubmit('username', error.message);
        }

        if (error.code === VISITOR_REGISTER_USERNAME_TAKEN_CODE) {
          return rejectSubmit('username', VISITOR_REGISTER_USERNAME_TAKEN_ERROR_MESSAGE, { isUsernameTaken: true });
        }

        if (error.code === VISITOR_REGISTER_RATE_LIMIT_CODE) {
          return rejectSubmit('form', VISITOR_REGISTER_RATE_LIMIT_ERROR_MESSAGE);
        }

        if (error.code === VISITOR_REGISTER_TERMS_NOT_ACCEPTED_CODE) {
          setIsTermsAccepted(false);
          return rejectSubmit('terms', VISITOR_REGISTER_TERMS_NOT_ACCEPTED_ERROR_MESSAGE);
        }

        if (error.code === VISITOR_REGISTER_TERMS_VERSION_OUTDATED_CODE) {
          await handleRetryTerms();
          return rejectSubmit('terms', VISITOR_REGISTER_TERMS_VERSION_OUTDATED_ERROR_MESSAGE);
        }
      }

      logError('来場者新規登録送信処理に失敗しました。', {
        username,
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      // RPCが失敗した場合は通信起因を優先表示し、原因不明時のみ一般エラーを表示する
      return rejectSubmit(
        'form',
        error instanceof AppError ? VISITOR_REGISTER_CHECK_ERROR_MESSAGE : VISITOR_REGISTER_FAILED_ERROR_MESSAGE,
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isTermsAccepted,
    isTermsLoading,
    handleRetryTerms,
    password,
    passwordConfirmation,
    rejectSubmit,
    terms,
    termsErrorMessage,
    username,
  ]);

  /** 登録ボタン無効状態（未同意では無効にせず、押した時に同意チェックの直下で案内する） */
  const isSubmitDisabled = isSubmitting
    || isTermsLoading
    || !terms
    || Boolean(termsErrorMessage);

  return {
    username,
    password,
    passwordConfirmation,
    terms,
    isTermsLoading,
    termsErrorMessage,
    isTermsAccepted,
    isTermsModalVisible,
    formError,
    isSubmitting,
    isSubmitDisabled,
    handleUsernameChange,
    handlePasswordChange,
    handlePasswordConfirmationChange,
    handleToggleTermsAccepted,
    handleOpenTermsModal,
    handleCloseTermsModal,
    handleRetryTerms,
    submitRegister,
  };
};
